import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Paper,
  Typography,
  Button,
  MenuItem,
  TextField,
  Box,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogActions,
} from '@mui/material';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  doc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth, signOut } from 'firebase/auth';
import WorkshopCard from './WorkshopCard';
import WorkshopModal from './WorkshopModal';
import { logUserAction } from './logActions';
import { days, categories } from '../constants';
import wtorek from '../assets/wtorek.webp';
import sroda from '../assets/sroda.webp';
import czwartek from '../assets/czwartek.webp';
import piatek from '../assets/piatek.webp';
import sobota from '../assets/sobota.webp';
import niedziela from '../assets/niedziela.webp';

const dayImages = {
  Wtorek: wtorek,
  Środa: sroda,
  Czwartek: czwartek,
  Piątek: piatek,
  Sobota: sobota,
  Niedziela: niedziela,
};

const Calendar = ({ role, setRole }) => {
  const auth = getAuth();
  const [workshops, setWorkshops] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [workshopToEdit, setWorkshopToEdit] = useState(null);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'workshops'), (snapshot) => {
      const fetchedWorkshops = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setWorkshops(sortWorkshops(fetchedWorkshops));
    });

    return () => unsubscribe();
  }, []);

  const sortWorkshops = (workshops) => {
    return [...workshops].sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
  };

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => {
    setModalOpen(false);
    setWorkshopToEdit(null);
  };

  const approveWorkshop = async (id) => {
    try {
      const user = auth.currentUser;
      const workshop = workshops.find((w) => w.id === id);
      const workshopRef = doc(db, 'workshops', id);

      await updateDoc(workshopRef, { isApproved: true });
      await logUserAction('approve', { ...workshop, isApproved: true }, user);
      setSnackbar({
        open: true,
        message: 'Warsztat zatwierdzony.',
        severity: 'success',
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas zatwierdzania warsztatu.',
        severity: 'error',
      });
    }
  };

  const addWorkshop = async (newWorkshop) => {
    try {
      const user = auth.currentUser;
      const docRef = await addDoc(collection(db, 'workshops'), {
        ...newWorkshop,
        isApproved: true,
      });

      await logUserAction('add', { ...newWorkshop, id: docRef.id }, user);
      handleCloseModal();
      setSnackbar({
        open: true,
        message: 'Warsztat dodany.',
        severity: 'success',
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas dodawania warsztatu.',
        severity: 'error',
      });
    }
  };

  const editWorkshop = (workshop) => {
    setWorkshopToEdit(workshop);
    handleOpenModal();
  };

  const saveWorkshop = async (updatedWorkshop) => {
    try {
      const user = auth.currentUser;
      const previousWorkshop = workshops.find(
        (w) => w.id === updatedWorkshop.id,
      );

      const { id, ...payload } = updatedWorkshop;

      const hasChanges = previousWorkshop && Object.keys(payload).some(
        (key) => payload[key] !== previousWorkshop[key],
      );

      if (!hasChanges) {
        handleCloseModal();
        setSnackbar({
          open: true,
          message: 'Brak zmian do zapisania.',
          severity: 'info',
        });
        return;
      }

      const workshopRef = doc(db, 'workshops', id);
      await updateDoc(workshopRef, payload);

      await logUserAction('edit', updatedWorkshop, user, previousWorkshop);
      handleCloseModal();
      setSnackbar({
        open: true,
        message: 'Warsztat zaktualizowany.',
        severity: 'success',
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas edycji warsztatu.',
        severity: 'error',
      });
    }
  };

  const requestDeleteWorkshop = (id) => {
    setDeleteConfirm({ open: true, id });
  };

  const confirmDeleteWorkshop = async () => {
    const id = deleteConfirm.id;
    setDeleteConfirm({ open: false, id: null });
    try {
      const user = auth.currentUser;
      const workshopToDelete = workshops.find((w) => w.id === id);
      await deleteDoc(doc(db, 'workshops', id));
      await logUserAction('delete', workshopToDelete, user);
      setSnackbar({
        open: true,
        message: 'Warsztat usunięty.',
        severity: 'success',
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas usuwania warsztatu.',
        severity: 'error',
      });
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setRole(null);
  };

  const handleDayChange = (event) => {
    setSelectedDay(event.target.value);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const filteredWorkshops = workshops.filter((workshop) => {
    const matchesDay = !selectedDay || workshop.day === selectedDay;

    const matchesCategory =
      !selectedCategory ||
      (selectedCategory === 'notApproved'
        ? workshop.isApproved === false
        : workshop.category === selectedCategory);

    const isVisible = role === 'organizer' || workshop.isApproved === true;

    return matchesDay && matchesCategory && isVisible;
  });

  return (
    <>
      <Box
        sx={{
          textAlign: 'center',
          marginTop: 2,
          marginBottom: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            color: '#fff',
            fontFamily: 'Roboto, sans-serif',
            textShadow: '0 0 6px rgba(34,197,94,0.7), 0 0 14px rgba(34,197,94,0.35)',
          }}
        >
          XIX Festiwal Enklawa
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          marginBottom: 2,
        }}
      >
        {role === 'organizer' && (
          <>
            <Button
              variant="contained"
              sx={{ backgroundColor: 'white', color: 'black' }}
              onClick={handleOpenModal}
            >
              Dodaj warsztat
            </Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: '#22c55e', color: 'black' }}
              onClick={handleLogout}
            >
              Wyloguj
            </Button>
          </>
        )}

        <TextField
          select
          label="Dzień"
          value={selectedDay}
          onChange={handleDayChange}
          sx={{ minWidth: '150px', backgroundColor: 'white' }}
        >
          <MenuItem value="">
            <em>Wszystkie dni</em>
          </MenuItem>
          {days.map((day) => (
            <MenuItem key={day.value} value={day.value}>
              {day.text}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Kategoria"
          value={selectedCategory}
          onChange={handleCategoryChange}
          sx={{ minWidth: '150px', backgroundColor: 'white' }}
        >
          <MenuItem value="">
            <em>Wszystkie kategorie</em>
          </MenuItem>
          {role === 'organizer' && (
            <MenuItem value="notApproved">Niezatwierdzone</MenuItem>
          )}
          {categories.map((category) => (
            <MenuItem key={category.name} value={category.name}>
              {category.name}
            </MenuItem>
          ))}
        </TextField>
        {/* <Button
          variant="contained"
          sx={{ backgroundColor: '#22c55e', color: 'black' }}
          onClick={() => navigate('/punktacja')}
        >
          Punktacja domów
        </Button> */}
      </Box>
      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        {days.map((day) => (
          <Grid item xs={12} sm={6} md={2} key={day.value}>
            <Box
              sx={{
                backgroundColor: '#2e3440',
                padding: '0',
                marginBottom: '16px',
                position: 'sticky',
                top: 0,
                zIndex: 1000,
              }}
            >
              <Box
                component="img"
                // src={[day.text]}
                src={dayImages[day.text]}
                alt={day.text}
                sx={{
                  padding: '0',
                  margin: '0',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            </Box>
            <Paper
              sx={{
                textAlign: 'center',
                height: '100%',
                backgroundColor: '#222222',
                color: '#fff',
              }}
            >
              {filteredWorkshops
                .filter((workshop) => workshop.day === day.value)
                .map((workshop) => (
                  <WorkshopCard
                    key={workshop.id}
                    workshop={workshop}
                    role={role}
                    onEdit={
                      role === 'organizer' ? () => editWorkshop(workshop) : null
                    }
                    onDelete={
                      role === 'organizer'
                        ? () => requestDeleteWorkshop(workshop.id)
                        : null
                    }
                    onApprove={() => approveWorkshop(workshop.id)}
                  />
                ))}
            </Paper>
          </Grid>
        ))}
      </Grid>
      <WorkshopModal
        open={modalOpen}
        handleClose={handleCloseModal}
        saveWorkshop={workshopToEdit ? saveWorkshop : addWorkshop}
        workshopToEdit={workshopToEdit}
        isOrganizer={true}
      />
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null })}
      >
        <DialogTitle>
          Na pewno chcesz usunąć {' '}
          <Box component="span" sx={{ fontWeight: 600 }}>
            „{workshops.find((w) => w.id === deleteConfirm.id)?.title || ''}”?
          </Box>
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, id: null })}>
            Anuluj
          </Button>
          <Button onClick={confirmDeleteWorkshop} color="error"  variant="contained">
            Usuń
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Calendar;
